import { useEffect, useState } from 'react';

import { getAuthTransport } from '@/lib/api/authTransport';
import { runtimeConfig } from '@/lib/config';

/** 현장 게시용 A4 안내문의 글 — 큰 제목 · 현장명 · QR 아래 짧은 줄들. */
export type QrPoster = { title: string; siteName: string; lines: string[] };

/**
 * QR 창 제목 줄의 단추 묶음(DOC-136 · 사용자 결정 2026-10-27 「QR 은 다운 말고도 여러 활용」) — 이수확인서 · TBM 참여자 서명 ·
 * 아차사고/의견 QR 창이 함께 쓴다.
 *
 * <ul>
 *   <li>링크 복사 · 공유 — 휴대폰이면 공유창(카카오톡 · 문자 …), 아니면 복사로 대신한다.</li>
 *   <li>크게 보기 — QR 을 화면 가득. 조회장 모니터 · 태블릿에서 바로 찍게.</li>
 *   <li>A4 안내문 — 서버가 구운 PDF 를 내려받는다(SafeQrPosterController). 화면의 QR 그림을 그대로 싣는다.</li>
 *   <li>그림 저장 — QR PNG.</li>
 * </ul>
 */
export function QrActions({ url, qr, name, poster }: { url: string | null; qr: string | null; name: string; poster: QrPoster }) {
  const [copied, setCopied] = useState(false);
  const [big, setBig] = useState(false);
  const [making, setMaking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard?.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  const share = async () => {
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${poster.siteName} ${poster.title}`.trim(), url });
      } catch {
        // 사람이 공유창을 닫은 것 — 할 일 없다.
      }
      return;
    }
    await copy();
  };
  const saveImage = () => {
    if (!qr) return;
    const a = document.createElement('a');
    a.href = qr;
    a.download = `${name}_QR.png`;
    a.click();
  };
  const downloadPoster = async () => {
    if (!qr) return;
    setMaking(true);
    setFailed(null);
    try {
      await fetchPoster({ ...poster, qrImage: qr, url, fileName: name });
    } catch (cause) {
      setFailed(cause instanceof Error ? cause.message : '안내문을 만들지 못했습니다.');
    } finally {
      setMaking(false);
    }
  };

  return (
    <>
      <button className="btn-secondary" disabled={!url} onClick={() => void copy()}>
        {copied ? '복사됨' : '링크 복사'}
      </button>
      <button className="btn-secondary" disabled={!url} onClick={() => void share()}>
        공유
      </button>
      <button className="btn-secondary" disabled={!qr} onClick={() => setBig(true)}>
        크게 보기
      </button>
      <button className="btn-secondary" disabled={!qr || making} onClick={() => void downloadPoster()} title={failed ?? undefined}>
        {making ? '만드는 중…' : failed ? 'A4 안내문 (다시)' : 'A4 안내문'}
      </button>
      <button className="btn-secondary" disabled={!qr} onClick={saveImage}>
        그림 저장
      </button>
      {big && qr && <BigQr qr={qr} poster={poster} onClose={() => setBig(false)} />}
    </>
  );
}

/** 화면 가득 QR — 누르거나 Esc 로 닫는다. */
function BigQr({ qr, poster, onClose }: { qr: string; poster: QrPoster; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-label="QR 크게 보기"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2vh',
        cursor: 'zoom-out',
      }}
    >
      <div style={{ fontSize: '2.4vh', color: '#475569' }}>{poster.siteName}</div>
      <div style={{ fontSize: '4.6vh', fontWeight: 800, color: '#0f172a' }}>{poster.title}</div>
      <img src={qr} alt="QR" style={{ width: '68vmin', height: '68vmin', imageRendering: 'pixelated' }} />
    </div>
  );
}

/** A4 안내문 PDF — 서버가 굽고 보던 탭 그대로 내려받는다. 파일명은 서버가 Content-Disposition 에 싣는다. */
async function fetchPoster(body: QrPoster & { qrImage: string; url: string | null; fileName: string }): Promise<void> {
  const transport = getAuthTransport();
  const response = await fetch(`${runtimeConfig().apiBaseUrl}/v1/safe/qr-poster`, {
    method: 'POST',
    credentials: transport.credentials,
    headers: { ...transport.authHeaders({ unsafe: true }), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`안내문을 만들지 못했습니다. (HTTP ${response.status})`);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = encoded ? decodeURIComponent(encoded) : `${body.fileName}_QR안내문.pdf`;
  link.click();
  URL.revokeObjectURL(link.href);
}
